import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FiHeart, FiUser, FiMenu, FiX, FiShoppingCart, FiLogOut, FiChevronDown } from 'react-icons/fi';
import { logout } from '../../store/slices/authSlice';
import { useRegion } from '../../utils/RegionContext';
import { COUNTRIES } from '../../utils/region';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  // Shopping-region sub-section inside the user dropdown is collapsed by
  // default so the menu stays compact — the country list only renders
  // after the user explicitly taps the "Shopping region" toggle.
  const [isRegionSectionOpen, setIsRegionSectionOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { items } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userMenuRef = useRef(null);
  const regionMenuRef = useRef(null);
  const { region, currency, setRegion } = useRegion();
  const currentCountry = COUNTRIES.find((c) => c.code === region) || COUNTRIES[0];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile + user menus on every route change so users aren't trapped.
  useEffect(() => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsRegionOpen(false);
    setIsRegionSectionOpen(false);
  }, [pathname]);

  // Collapse the in-dropdown region section whenever the user menu closes,
  // so the next time it opens the user sees the compact view first.
  useEffect(() => {
    if (!isUserMenuOpen) setIsRegionSectionOpen(false);
  }, [isUserMenuOpen]);

  // Close user menu on outside click + Escape key (touch + keyboard friendly).
  useEffect(() => {
    if (!isUserMenuOpen) return undefined;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    const onKey = (e) => e.key === 'Escape' && setIsUserMenuOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isUserMenuOpen]);

  // Same outside-click/Escape handling for the region menu.
  useEffect(() => {
    if (!isRegionOpen) return undefined;
    const onClick = (e) => {
      if (regionMenuRef.current && !regionMenuRef.current.contains(e.target)) {
        setIsRegionOpen(false);
      }
    };
    const onKey = (e) => e.key === 'Escape' && setIsRegionOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isRegionOpen]);

  const handleLogout = () => {
    dispatch(logout());
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const cartLabel = cartItemCount > 0 ? `Cart, ${cartItemCount} items` : 'Cart';

  return (
    <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
      <div className="container">
        <div className="header__inner">
          <button
            className="header__menu-btn"
            onClick={() => setIsMenuOpen((v) => !v)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="header-nav"
          >
            {isMenuOpen ? <FiX size={24} aria-hidden="true" /> : <FiMenu size={24} aria-hidden="true" />}
          </button>

          <Link to="/" className="header__logo" aria-label="Shopyalley home">
            {/* Intrinsic dimensions of logo.jpeg (716x564) are declared on
                the <img> tag so the browser can reserve the correct aspect
                ratio for CLS-safe layout before the bitmap decodes. CSS
                controls the rendered size. */}
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo.jpeg`}
              className="header__logo-img"
              alt="Shopyalley logo"
              width="716"
              height="564"
            />
            <span className="header__logo-text">Shopyalley</span>
          </Link>

          <nav
            id="header-nav"
            className={`header__nav ${isMenuOpen ? 'header__nav--open' : ''}`}
            aria-label="Primary"
          >
            <Link to="/">Home</Link>
            <Link to="/shop">Shop All</Link>
            <Link to="/shop/clothes">Clothes</Link>
            <Link to="/shop/jewellery">Jewellery</Link>
            <Link to="/shop/footwear">Footwear</Link>
            <Link to="/contact">Contact</Link>
            {/*
              Account / cart / region actions used to live here as a drawer
              fallback when the top-bar avatar + region pill were hidden on
              phones. We now keep the avatar dropdown visible on every screen
              size, so duplicating its contents inside the drawer just creates
              a second-place-to-look problem (users tap Wishlist in the drawer
              and Wishlist in the avatar menu interchangeably, and the two
              click handlers stayed in sync only by accident). Removing the
              block fixes the duplicate Cart / Profile / Orders / Wishlist /
              Admin Dashboard / Logout / Shopping region entries reported on
              phones + tablets. Guest users still see Login + region pill in
              the top bar, so no functionality is lost.
            */}
          </nav>

          <div className="header__actions">
            {/* Region / currency chip — shown in the top bar ONLY for guests.
                Signed-in users get the region selector inside their account
                dropdown (and inside the hamburger drawer on phones), so the
                top bar stays uncluttered. */}
            {!isAuthenticated && (
              <div className="header__region" ref={regionMenuRef}>
                <button
                  type="button"
                  className="header__region-btn"
                  aria-label={`Region: ${currentCountry.name}, currency ${currency}. Change region.`}
                  aria-haspopup="menu"
                  aria-expanded={isRegionOpen}
                  onClick={() => setIsRegionOpen((v) => !v)}
                >
                  <span aria-hidden="true">{currentCountry.flag}</span>
                  <span className="header__region-currency">{currency}</span>
                </button>
                {isRegionOpen && (
                  <div className="header__dropdown header__dropdown--region" role="menu">
                    <div className="header__dropdown-header">Shopping region</div>
                    {COUNTRIES.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        role="menuitemradio"
                        aria-checked={c.code === region}
                        className={c.code === region ? 'header__region-option--active' : ''}
                        onClick={() => { setRegion(c.code); setIsRegionOpen(false); }}
                      >
                        <span aria-hidden="true">{c.flag}</span> {c.name}
                        <span className="header__region-meta">{c.currency}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isAuthenticated ? (
              <>
                <Link to="/wishlist" className="header__action-btn" aria-label="Wishlist">
                  <FiHeart size={20} aria-hidden="true" />
                </Link>
                <Link to="/cart" className="header__action-btn header__cart-btn" aria-label={cartLabel}>
                  <FiShoppingCart size={20} aria-hidden="true" />
                  {cartItemCount > 0 && (
                    <span className="header__cart-badge" aria-hidden="true">
                      {cartItemCount > 99 ? '99+' : cartItemCount}
                    </span>
                  )}
                </Link>
                <div className="header__user-menu" ref={userMenuRef}>
                  <button
                    className="header__action-btn"
                    aria-label="User menu"
                    aria-haspopup="menu"
                    aria-expanded={isUserMenuOpen}
                    onClick={() => setIsUserMenuOpen((v) => !v)}
                  >
                    <FiUser size={20} aria-hidden="true" />
                  </button>
                  {isUserMenuOpen && (
                    <div className="header__dropdown" role="menu">
                      <div className="header__dropdown-header">Hi, {user?.firstName}</div>
                      <Link to="/profile" role="menuitem">My Profile</Link>
                      <Link to="/orders" role="menuitem">My Orders</Link>
                      <Link to="/wishlist" role="menuitem">Wishlist</Link>
                      {user?.role === 'admin' && (
                        <Link to="/admin" role="menuitem">Admin Dashboard</Link>
                      )}
                      {user?.role === 'delivery_partner' && (
                        <Link to="/delivery" role="menuitem">Delivery Dashboard</Link>
                      )}
                      {/* Region picker — collapsed by default. Tapping the
                          header reveals the country list with the active
                          country marked. Keeps the dropdown short for users
                          who don't need to change region. */}
                      <button
                        type="button"
                        className="header__dropdown-subheader header__dropdown-region-toggle"
                        aria-expanded={isRegionSectionOpen}
                        aria-controls="header-region-list"
                        onClick={() => setIsRegionSectionOpen((v) => !v)}
                      >
                        <span>Shopping region</span>
                        <span className="header__dropdown-region-toggle__meta" aria-hidden="true">
                          <span>{currentCountry.flag}</span>
                          <span>{currency}</span>
                          <FiChevronDown
                            size={14}
                            className={`header__dropdown-region-toggle__chevron ${isRegionSectionOpen ? 'is-open' : ''}`}
                          />
                        </span>
                      </button>
                      {isRegionSectionOpen && (
                        <div id="header-region-list" role="group" aria-label="Shopping region">
                          {COUNTRIES.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              role="menuitemradio"
                              aria-checked={c.code === region}
                              className={`header__dropdown-region ${c.code === region ? 'header__region-option--active' : ''}`}
                              onClick={() => { setRegion(c.code); setIsUserMenuOpen(false); }}
                            >
                              <span aria-hidden="true">{c.flag}</span> {c.name}
                              <span className="header__region-meta">{c.currency}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <button onClick={handleLogout} role="menuitem" className="header__dropdown-logout">
                        <FiLogOut size={16} aria-hidden="true" /> Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm">Login</Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
